import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MultiSelect } from "@/components/ui";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import i18n from "@/i18n";
import type { Activity } from "@/types";
import { cn } from "@/lib/utils";

interface BookingsActivityFilterProps {
  activities: Activity[];
  bookingCountsByActivityId: ReadonlyMap<string, number>;
  selectedActivityIds: string[];
  onSelectActivities: (activityIds: string[]) => void;
  className?: string;
}

function activityLabel(activity: Activity): string {
  return resolveCatalogTranslation(
    activity.name_translations,
    i18n.language,
  );
}

export function BookingsActivityFilter({
  activities,
  bookingCountsByActivityId,
  selectedActivityIds,
  onSelectActivities,
  className,
}: BookingsActivityFilterProps) {
  const { t } = useTranslation("bookings");
  const { t: tComponents } = useTranslation("components");

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      const countDiff =
        (bookingCountsByActivityId.get(b.id) ?? 0) -
        (bookingCountsByActivityId.get(a.id) ?? 0);
      if (countDiff !== 0) {
        return countDiff;
      }
      return activityLabel(a).localeCompare(activityLabel(b), i18n.language);
    });
  }, [activities, bookingCountsByActivityId]);

  const options = useMemo(
    () =>
      sortedActivities.map((activity) => {
        const count = bookingCountsByActivityId.get(activity.id) ?? 0;
        return {
          value: activity.id,
          label: t("activityFilter.optionWithCount", {
            name: activityLabel(activity),
            count,
          }),
        };
      }),
    [bookingCountsByActivityId, sortedActivities, t],
  );

  if (activities.length === 0) {
    return null;
  }

  return (
    <MultiSelect
      label={t("activityFilter.label")}
      className={cn(className)}
      options={options}
      value={selectedActivityIds}
      onChange={onSelectActivities}
      placeholderLabel={t("activityFilter.allActivities")}
      removeChipLabel={(optionLabel) =>
        tComponents("multiSelectRemoveChip", { label: optionLabel })
      }
    />
  );
}
